<template>
    <!-- Structured MCP `fetch` — the sub-second counterpart to `ask` (PLT-1919). Whether a
         path is readable is decided by the tenant's Work IQ MCP policy, so a denial is
         called out as a governance decision rather than shown as a generic error. -->
    <div class="flex flex-col gap-6">
        <p class="body-small text-valence-grey-750 -mt-2">
            Read Microsoft 365 entities directly by resource path, with no LLM in the loop. Runs on the same
            <span class="font-mono">WorkIQAgent.Ask</span> scope as the Prompt tab — no extra consent — but each path is allowed or denied by your tenant's Work IQ MCP policy.
        </p>

        <div v-if="!connected" class="rounded-xl border-2 border-[#E8E8E8] bg-white p-5 body-small text-valence-grey-750">
            Connect Work IQ on the Prompt tab to run structured lookups.
        </div>

        <template v-else>
            <!-- What the MCP surface demands and exposes. -->
            <div class="flex flex-col gap-2 p-5 bg-white rounded-xl border-2 border-[#E8E8E8]">
                <div class="flex items-center justify-between gap-3">
                    <div class="body-bold text-valence-grey-900">MCP surface</div>
                    <BaseButton theme="tertiary" size="sm" :disabled="infoLoading" @click="loadInfo">
                        {{ infoLoading ? "Checking…" : "Check surface" }}
                    </BaseButton>
                </div>

                <div v-if="infoError" class="rounded-md border border-red-300 bg-red-50 px-4 py-2 body-small text-red-800 whitespace-pre-wrap break-words">
                    {{ infoError }}
                </div>

                <template v-else-if="info">
                    <!-- Stacks below sm: a fixed label column plus break-all on long scope and
                         tool strings degrades to one character per line in a narrow column. -->
                    <dl class="flex flex-col gap-2 body-small text-valence-grey-750">
                        <div class="flex flex-col sm:flex-row sm:gap-2">
                            <dt class="text-valence-grey-500 sm:w-28 sm:flex-shrink-0">Endpoint</dt>
                            <dd class="font-mono break-words">{{ info.resource }}</dd>
                        </div>
                        <div class="flex flex-col sm:flex-row sm:gap-2">
                            <dt class="text-valence-grey-500 sm:w-28 sm:flex-shrink-0">Scopes</dt>
                            <dd class="font-mono break-words">{{ info.scopes_supported.join(", ") || "—" }}</dd>
                        </div>
                        <div v-if="info.server" class="flex flex-col sm:flex-row sm:gap-2">
                            <dt class="text-valence-grey-500 sm:w-28 sm:flex-shrink-0">Server</dt>
                            <dd>{{ info.server }}</dd>
                        </div>
                        <div v-if="info.tools.length" class="flex flex-col sm:flex-row sm:gap-2">
                            <dt class="text-valence-grey-500 sm:w-28 sm:flex-shrink-0">Tools ({{ info.tools.length }})</dt>
                            <dd class="font-mono break-words">{{ info.tools.join(", ") }}</dd>
                        </div>
                    </dl>
                </template>

                <div v-else class="body-small text-valence-grey-500">Check the surface to see the required scope and available tools.</div>
            </div>

            <!-- Path input + presets. -->
            <div class="flex flex-col gap-3 p-5 bg-white rounded-xl border-2 border-[#E8E8E8]">
                <div class="body-bold text-valence-grey-900">Fetch a resource path</div>

                <div class="flex flex-wrap gap-2">
                    <button
                        v-for="preset in PRESETS"
                        :key="preset.label"
                        type="button"
                        class="text-xs px-2 py-1 rounded border border-[#E8E8E8] text-valence-grey-750 hover:bg-valence-grey-50"
                        :data-testid="`workiq-fetch-preset-${preset.label}`"
                        @click="path = preset.path()">
                        {{ preset.label }}
                    </button>
                </div>

                <label for="workiq-fetch-path" class="flex flex-col gap-1">
                    <span class="sr-only">Resource path</span>
                    <textarea
                        id="workiq-fetch-path"
                        v-model="path"
                        rows="3"
                        spellcheck="false"
                        data-testid="workiq-fetch-path"
                        class="w-full rounded-lgr border-2 border-[#E8E8E8] p-3 font-mono text-xs"
                        placeholder="/me/messages?$top=5&$select=subject,from,body"></textarea>
                </label>

                <div class="flex items-center justify-between gap-3">
                    <span class="text-xs text-valence-grey-500">One path per line. Read-only — mutations are blocked by tenant policy by default.</span>
                    <BaseButton theme="primary" size="sm" :disabled="loading || !paths.length" @click="runFetch">
                        {{ loading ? "Fetching…" : "Fetch" }}
                    </BaseButton>
                </div>
            </div>

            <!-- Result. -->
            <div v-if="policyDenied" class="rounded-xl border-2 border-amber-300 bg-amber-50 p-5 flex flex-col gap-2">
                <div class="body-small-semibold text-amber-900">Blocked by tenant policy</div>
                <p class="body-small text-amber-900">
                    Your tenant's Work IQ MCP policy doesn't allow this path. That's an admin-center setting (Agents → Tools → Work IQ MCP → Policy), not a consent or token problem
                    — reconnecting won't change it, and changes can take up to 24h to apply.
                </p>
                <pre class="text-xs whitespace-pre-wrap break-words text-amber-900">{{ error }}</pre>
            </div>

            <div
                v-else-if="error"
                class="rounded-md border border-red-300 bg-red-50 px-4 py-2 body-small text-red-800 whitespace-pre-wrap break-words"
                data-testid="workiq-fetch-error">
                {{ error }}
            </div>

            <div v-if="results.length" class="flex flex-col gap-3 p-5 bg-white rounded-xl border-2 border-[#E8E8E8]">
                <div class="flex items-center justify-between gap-3">
                    <div class="body-bold text-valence-grey-900">Result</div>
                    <span class="text-xs text-valence-grey-500" data-testid="workiq-fetch-elapsed">{{ elapsedMs }} ms round-trip</span>
                </div>

                <div v-for="(result, index) in results" :key="index" class="flex flex-col gap-2 border-t border-gray-100 pt-2 first:border-t-0 first:pt-0">
                    <div class="flex items-center flex-wrap gap-2">
                        <span class="font-mono text-xs text-valence-grey-750 break-all">{{ entityUrls[index] }}</span>
                        <span
                            class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                            :class="result.status_code === 200 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                            {{ result.status_code }}
                        </span>
                        <span class="text-xs text-valence-grey-500">{{ result.row_count }} {{ result.row_count === 1 ? "entity" : "entities" }}</span>
                        <span v-if="result.body_chars" class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                            Full bodies · {{ result.body_chars.toLocaleString() }} chars
                        </span>
                    </div>
                    <div v-if="result.error" class="body-small text-red-800 whitespace-pre-wrap break-words">{{ result.error }}</div>
                    <pre class="text-xs bg-valence-grey-50 border border-[#E8E8E8] rounded-lg p-3 overflow-auto max-h-96">{{ pretty(result.data) }}</pre>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { getCookie } from "/js/utils";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import { computed, ref } from "vue";

interface McpInfo {
    resource: string;
    scopes_supported: string[];
    tools: string[];
    server: string;
    error?: string;
}

interface FetchResultRow {
    status_code: number | null;
    error: string | null;
    row_count: number;
    body_chars: number;
    data: unknown;
}

const props = defineProps<{
    connected: boolean;
    mcpInfoUrl: string;
    fetchUrl: string;
}>();

// `body` is in $select deliberately: the open question this tab answers is whether the
// structured surface returns real message content or only metadata.
const MESSAGE_FIELDS = "subject,from,receivedDateTime,isRead,bodyPreview,body";

function isoDaysFromNow(days: number): string {
    return new Date(Date.now() + days * 86_400_000).toISOString();
}

const PRESETS = [
    { label: "recent mail", path: () => `/me/messages?$top=5&$orderby=receivedDateTime desc&$select=${MESSAGE_FIELDS}` },
    { label: "unread mail", path: () => `/me/messages?$top=5&$filter=isRead eq false&$select=${MESSAGE_FIELDS}` },
    { label: "calendar ±14d", path: () => `/me/calendarView?startDateTime=${isoDaysFromNow(-14)}&endDateTime=${isoDaysFromNow(14)}&$top=25` },
    { label: "chats", path: () => "/me/chats?$top=10" },
    { label: "people", path: () => "/me/people?$top=10" },
    { label: "me", path: () => "/me" },
] as const;

const path = ref(PRESETS[0].path());
const info = ref<McpInfo | null>(null);
const infoError = ref("");
const infoLoading = ref(false);

const entityUrls = ref<string[]>([]);
const results = ref<FetchResultRow[]>([]);
const elapsedMs = ref(0);
const error = ref("");
const policyDenied = ref(false);
const loading = ref(false);

const connected = computed(() => props.connected);
const paths = computed(() =>
    path.value
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
);

function pretty(value: unknown): string {
    return JSON.stringify(value, null, 2);
}

async function loadInfo() {
    infoLoading.value = true;
    infoError.value = "";
    try {
        const { data, response } = await useFetch(props.mcpInfoUrl).get().json();
        if (response.value?.ok) {
            info.value = data.value as McpInfo;
            infoError.value = info.value?.error ?? "";
        } else {
            info.value = null;
            infoError.value = data.value?.error || "Failed to read the MCP surface.";
        }
    } finally {
        infoLoading.value = false;
    }
}

async function runFetch() {
    loading.value = true;
    error.value = "";
    policyDenied.value = false;
    results.value = [];
    try {
        const { data, response } = await useFetch(props.fetchUrl, {
            headers: { "Content-Type": "application/json", "X-CSRFToken": getCookie("csrftoken") ?? "" },
        })
            .post({ entity_urls: paths.value })
            .json();

        if (response.value?.ok) {
            entityUrls.value = data.value?.entity_urls ?? paths.value;
            results.value = data.value?.results ?? [];
            elapsedMs.value = data.value?.elapsed_ms ?? 0;
        } else {
            policyDenied.value = Boolean(data.value?.policy_denied);
            error.value = data.value?.error || "Fetch failed.";
        }
    } finally {
        loading.value = false;
    }
}
</script>
